import React, { useState } from 'react';

import { useGetEvent } from '@vlabs/api/artemy/client';
import type { EventId } from '@vlabs/api/artemy/model/EventId';
import { proxyArtemyUrl } from '@vlabs/api/axios/point2-client';
import { useImage } from '@vlabs/shared/hooks';
import { ButtonSwitch, ButtonSwitchGroup } from '@vlabs/uikit';
import type { WithDataTestid, WithClassNameProp } from '@vlabs/uikit/types';
import cn from 'classnames';

import { FullFrameDetectionsWithSettingsPoint3 } from '@vlabs/monokit/entities/event-details/full-frame-detections-with-settings';
import { useEventApi } from '@vlabs/monokit/entities/events/api';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { useVmsFromSourceName } from '@vlabs/monokit/widgets/video-archive';

import st from './EventMediaViewer.module.sass';
import { useEventMediaViewerTranslation } from './i18n';

type Tab = 'frame' | 'videoArchive';

const isImageOrigin = (imageOrigin?: string | null): imageOrigin is string => (
  !!imageOrigin && /^\/(1|6)\/images\//u.test(imageOrigin)
);

const getImageUrl = (imageOrigin?: string | null): string | undefined => {
  if (!isImageOrigin(imageOrigin)) return undefined;

  return proxyArtemyUrl(imageOrigin.replace(/^\/6\/images\//u, '/1/images/'));
};

interface EventMediaViewerPoint3Props extends WithDataTestid, WithClassNameProp {
  eventId: EventId;
  canViewVideoArchive?: boolean;
}

const displayName = 'EventMediaViewerPoint3';
const testid = buildTestid(displayName);

interface VideoArchiveTabProps {
  source?: string | null;
  canViewVideoArchive: boolean;
  tab: Tab;
  onTabChange: (tab: Tab) => void;
  frameDisabled: boolean;
  testId: string | undefined;
}

function VideoArchiveTab({
  source = undefined,
  canViewVideoArchive,
  tab,
  onTabChange,
  frameDisabled,
  testId: dataTestId,
}: VideoArchiveTabProps): React.JSX.Element {
  const { t } = useEventMediaViewerTranslation();
  const { vmsSource } = useVmsFromSourceName(source);

  if (!vmsSource || !canViewVideoArchive) {
    return <h5>{t('tab', { context: 'frame' })}</h5>;
  }

  return (
    <ButtonSwitchGroup
      onChange={onTabChange}
      size="small"
      value={tab}
      variant="square"
      {...testid(dataTestId)}
    >
      <ButtonSwitch
        disabled={frameDisabled}
        value="frame"
      >
        {t('tab', { context: 'frame' })}
      </ButtonSwitch>

      <ButtonSwitch
        disabled
        value="videoArchive"
      >
        {t('tab', { context: 'video_archive' })}
      </ButtonSwitch>
    </ButtonSwitchGroup>
  );
}

function EventMediaViewerPoint3({
  eventId,
  className = undefined,
  'data-testid': dataTestId = undefined,
  canViewVideoArchive = true,
}: EventMediaViewerPoint3Props): React.JSX.Element {
  const { t } = useEventMediaViewerTranslation();
  const api = useEventApi();

  const [tab, setTab] = useState<Tab>('frame');

  const { data: event } = useGetEvent(
    eventId ?? '',
    { target: ['face_detections', 'body_detections'] },
    { query: { enabled: !!eventId } },
  );

  const detectionWithFullFrame = event?.face_detections?.find(({ image_origin }) => isImageOrigin(image_origin))
      ?? event?.body_detections?.find(({ image_origin }) => isImageOrigin(image_origin));
  const fullFrameUrl = getImageUrl(detectionWithFullFrame?.image_origin);
  const fullFrame = useImage(fullFrameUrl);

  return (
    <div
      className={cn(st.Root, className)}
      {...testid(dataTestId)}
    >
      {!!api.capabilities.videoArchive && (
        <VideoArchiveTab
          canViewVideoArchive={canViewVideoArchive}
          frameDisabled={!fullFrame.url}
          onTabChange={setTab}
          source={event?.source}
          tab={tab}
          testId={dataTestId}
        />
      )}
      {!api.capabilities.videoArchive && <h5>{t('tab', { context: 'frame' })}</h5>}

      {tab === 'frame' && !!fullFrame.url && <FullFrameDetectionsWithSettingsPoint3 eventId={eventId} />}
      {/* FIXME табу с видеоархивом добавить в отдельной задаче */}

      {!fullFrame.url && <span className={st.NoData}>{t('no_data')}</span>}
    </div>
  );
}

EventMediaViewerPoint3.displayName = displayName;

export { EventMediaViewerPoint3 };
